import { useEffect, useMemo } from 'react';
import {
  resolveInternalNetworkCredentialVerificationReadiness,
  shouldPollInternalNetworkCredentialVerificationReadiness,
} from '@terra/ui-design-system';
import { useInternalNetworkDiscoveryReadiness } from '../../../../hooks/api/useInternalNetworkDiscoveryStatus';
import { useInternalNetworkEnrollments } from '../../../../hooks/api/useSurfaceSettings';

const WAITING_POLL_INTERVAL_MS = 5_000;

export function useInternalNetworkSensorConnectionReadiness({
  networkId,
  pollWhileWaiting,
}: {
  networkId: string;
  pollWhileWaiting: boolean;
}) {
  const enrollmentsQuery = useInternalNetworkEnrollments(networkId);
  const readinessQuery = useInternalNetworkDiscoveryReadiness(networkId);
  const enrollments = enrollmentsQuery.data?.data;
  const { refetch: refetchEnrollments } = enrollmentsQuery;
  const { refetch: refetchReadiness } = readinessQuery;
  const readiness = useMemo(
    () =>
      resolveInternalNetworkCredentialVerificationReadiness({
        enrollments: enrollments ?? [],
        discoveryReadiness: readinessQuery.data,
        readinessSource: 'sensor',
      }),
    [enrollments, readinessQuery.data],
  );
  const shouldPoll = shouldPollInternalNetworkCredentialVerificationReadiness({
    pollWhileWaiting,
    readiness,
  });

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refetchReadiness();
      void refetchEnrollments();
    }, WAITING_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refetchEnrollments, refetchReadiness, shouldPoll]);

  return {
    ...readiness,
    isLoading: enrollmentsQuery.isLoading || readinessQuery.isLoading,
  };
}
